// Hero — left col: headline + subtext + CTAs + dark live-stats card.
// Right col: auto-cycling pile of 6 OperatorCards (top swipes off every 3.4s, deck rotates).
// Source canonical: design-system/project/homepage/index.html § Hero component.

import { useEffect, useRef, useState } from 'react';
import OperatorCard from '../components/OperatorCard';
import { ROSTER } from '../data/roster';

export default function Hero() {
  const [live, setLive] = useState({ runs: 4891, spend: 247.32 });
  const [tick, setTick] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const mobileCarouselRef = useRef<HTMLDivElement>(null);

  // Auto-scroll mobile peek-carousel to centre the active card.
  // IMPORTANT: scroll the carousel itself (not scrollIntoView, which would
  // scroll ANY scrollable ancestor — including the section/page horizontally
  // and shift the headline off-screen).
  useEffect(() => {
    const root = mobileCarouselRef.current;
    if (!root) return;
    const target = root.children[tick % ROSTER.length] as HTMLElement | undefined;
    if (!target) return;
    const left = target.offsetLeft - (root.clientWidth - target.clientWidth) / 2;
    root.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }, [tick]);

  // Live stats counter
  useEffect(() => {
    const t = setInterval(() => setLive(s => ({
      runs: s.runs + Math.floor(Math.random() * 3),
      spend: +(s.spend + Math.random() * 0.12).toFixed(2),
    })), 2200);
    return () => clearInterval(t);
  }, []);

  // Pile cycle: swipe top card off, then advance index
  useEffect(() => {
    const cycle = setInterval(() => {
      setSwiping(true);
      setTimeout(() => { setTick(x => x + 1); setSwiping(false); }, 650);
    }, 3400);
    return () => clearInterval(cycle);
  }, []);

  return (
    <section
      style={{
        padding: '80px 40px 96px',
        background: '#1A1918',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Copper glow top-right */}
      <div
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'radial-gradient(circle at top right, rgba(212,131,94,0.18) 0%, rgba(212,131,94,0.05) 35%, transparent 65%)',
        }}
      />
      {/* Subtle grid */}
      <div
        style={{
          position: 'absolute', inset: 0, opacity: 0.20, pointerEvents: 'none',
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse at top right, black 20%, transparent 70%)',
          WebkitMaskImage: 'radial-gradient(ellipse at top right, black 20%, transparent 70%)',
        }}
      />

      <div
        className="hero-grid"
        style={{
          maxWidth: 1200, margin: '0 auto', position: 'relative',
          display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 56,
          alignItems: 'center',
        }}
      >
        {/* LEFT — headline column */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 22 }}>
            <span
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                padding: '5px 12px',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: 9999,
                fontFamily: 'IBM Plex Mono, monospace',
                fontSize: 11, color: '#C7C3BD',
              }}
            >
              <span
                style={{
                  width: 7, height: 7, borderRadius: 9999,
                  background: '#4ADE80',
                  boxShadow: '0 0 0 3px rgba(74,222,128,0.18)',
                }}
              />
              <span>3 roles open · 2 interviewing · 1 in training</span>
            </span>
          </div>

          <h1
            style={{
              fontFamily: 'Fraunces, serif',
              fontSize: 72, fontWeight: 400,
              fontVariationSettings: "'opsz' 144",
              letterSpacing: '-0.03em', lineHeight: 1.0,
              color: '#F5F4F2',
              margin: '0 0 24px',
            }}
          >
            Stop buying software.
            <br />
            <em style={{ fontStyle: 'italic', color: '#D4835E' }}>Hire the role.</em>
          </h1>

          <p
            style={{
              fontFamily: 'Inter Tight, system-ui, sans-serif',
              fontSize: 18, lineHeight: 1.55,
              color: '#C7C3BD',
              maxWidth: 520, margin: '0 0 32px',
            }}
          >
            <b style={{ color: '#F5F4F2' }}>AI employees, not dashboards.</b>{' '}
            Each one arrives with a résumé, a price per shift, and proof of past work. Hire the role; we run the rest.
          </p>

          <div className="hero-cta-row" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <a href="#waitlist" className="nav-cta" style={{ padding: '14px 26px', fontSize: 15 }}>Review applications →</a>
            <a href="#roster" className="nav-pill" style={{ padding: '14px 26px', fontSize: 15 }}>See the roster</a>
          </div>

          {/* Dark live-stats card */}
          <div
            className="hero-stats"
            style={{
              marginTop: 36,
              display: 'flex', gap: 22,
              padding: '16px 20px',
              background: '#232120',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 12,
              maxWidth: 480,
              fontFamily: 'IBM Plex Mono, monospace',
            }}
          >
            <div>
              <div style={{ fontSize: 10, color: '#908A82', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Runs this month</div>
              <div style={{ fontSize: 22, fontWeight: 600, color: '#F5F4F2', marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{live.runs.toLocaleString()}</div>
            </div>
            <div style={{ width: 1, background: 'rgba(255,255,255,0.08)' }} />
            <div>
              <div style={{ fontSize: 10, color: '#908A82', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Spend</div>
              <div style={{ fontSize: 22, fontWeight: 600, color: '#F5F4F2', marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>${live.spend.toFixed(2)}</div>
            </div>
            <div style={{ width: 1, background: 'rgba(255,255,255,0.08)' }} />
            <div>
              <div style={{ fontSize: 10, color: '#908A82', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Avg quality</div>
              <div style={{ fontSize: 22, fontWeight: 600, color: '#4ADE80', marginTop: 4 }}>0.87</div>
            </div>
          </div>
        </div>

        {/* RIGHT — DESKTOP: auto-cycling résumé pile */}
        <div className="hero-pile lg-only" style={{ position: 'relative', height: 620 }}>
          {ROSTER.map((op, i) => {
            const rank = (i - (tick % ROSTER.length) + ROSTER.length) % ROSTER.length;
            const isTop = rank === 0;
            const visible = rank < 5;
            const tilt = (rank - 1.5) * 1.5;
            const depthShadow = `0 ${10 + (5 - Math.min(rank, 5)) * 4}px 28px -12px rgba(26,25,23,${0.06 + (5 - Math.min(rank, 5)) * 0.04})`;
            return (
              <div
                key={op.id}
                className={`resume-card ${isTop && swiping ? 'swipe-out' : ''} ${!visible ? 'behind' : ''}`}
                style={{
                  top: visible ? rank * 80 : 5 * 80 + 40,
                  left: visible ? rank * 16 : 5 * 16 + 8,
                  transform: `rotate(${tilt}deg)`,
                  zIndex: 100 - rank,
                  boxShadow: depthShadow,
                  pointerEvents: isTop ? 'auto' : 'none',
                }}
              >
                <OperatorCard op={op} />
              </div>
            );
          })}

          {/* "candidates · cycling" hint pill */}
          <div
            style={{
              position: 'absolute', bottom: -8, left: 4 * 16 + 60,
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '6px 12px',
              background: '#232120',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 9999,
              fontFamily: 'IBM Plex Mono, monospace',
              fontSize: 10, color: '#C7C3BD',
              letterSpacing: '0.06em', textTransform: 'uppercase',
              boxShadow: '0 6px 18px -8px rgba(0,0,0,0.50)',
            }}
          >
            <span
              style={{
                width: 6, height: 6, borderRadius: 9999,
                background: '#D4835E',
                boxShadow: '0 0 0 3px rgba(212,131,94,0.20)',
              }}
            />
            {ROSTER.length} candidates · cycling
          </div>
        </div>

        {/* RIGHT — MOBILE: peek-carousel of 6 cards, auto-scrolls to active card on tick */}
        <div className="hero-pile-mobile sm-only">
          <div ref={mobileCarouselRef} className="h-carousel hero-peek">
            {ROSTER.map((op, i) => {
              const activeIdx = tick % ROSTER.length;
              const dist = (i - activeIdx + ROSTER.length) % ROSTER.length;
              // 0=active, 1=next, ROSTER.length-1=prev, others=far
              const role =
                dist === 0 ? 'active' :
                dist === 1 ? 'next' :
                dist === ROSTER.length - 1 ? 'prev' : 'far';
              return (
                <div
                  key={op.id}
                  className="resume-card hero-peek-card"
                  data-active={role === 'active' ? 'true' : undefined}
                  data-next={role === 'next' ? 'true' : undefined}
                  data-prev={role === 'prev' ? 'true' : undefined}
                  data-far={role === 'far' ? 'true' : undefined}
                >
                  <OperatorCard op={op} />
                </div>
              );
            })}
          </div>

          {/* Dot indicators */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            marginTop: 12,
          }}>
            {ROSTER.map((_, i) => {
              const active = i === tick % ROSTER.length;
              return (
                <span
                  key={i}
                  style={{
                    width: active ? 18 : 6, height: 6, borderRadius: 9999,
                    background: active ? '#D4835E' : 'rgba(255,255,255,0.22)',
                    transition: 'all 200ms ease',
                  }}
                />
              );
            })}
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            marginTop: 8,
            fontFamily: 'IBM Plex Mono, monospace',
            fontSize: 10, color: '#908A82',
            letterSpacing: '0.08em', textTransform: 'uppercase',
          }}>
            {String((tick % ROSTER.length) + 1).padStart(2, '0')} / {String(ROSTER.length).padStart(2, '0')} · swipe or watch
          </div>

          {/* Mobile live-stat caption — replaces the stats card on mobile */}
          <div className="hero-mobile-stat" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            marginTop: 14,
            fontFamily: 'IBM Plex Mono, monospace',
            fontSize: 12, color: '#C7C3BD',
            letterSpacing: '0.04em',
          }}>
            <span><b style={{ color: '#F5F4F2', fontVariantNumeric: 'tabular-nums' }}>{live.runs.toLocaleString()}</b> outputs this week</span>
            <span style={{ color: '#5A554F' }}>·</span>
            <span>quality <b style={{ color: '#4ADE80' }}>0.94</b></span>
          </div>
        </div>
      </div>
    </section>
  );
}
